import { WebSocketProgressReport } from '@/models/Communication'
import { StringArrayObject } from '@/models/GenericInterfaces'
import { Country, PimLocationQuery, Progress } from '@/models/Global'
import Message from '@/models/Message'
import { ECommerceSyncPayload, IgnorableRule, Site, SyncPreview } from '@/models/Sync'
import { useDefaultStore } from '@/store'
import ApiService from './ApiService'
import DataService from './DataService'

class SyncService {
  private get store() {
    return useDefaultStore()
  }

  private wsConnection: WebSocket | undefined

  /**
   * Syncs products matching the query to selected sites. Sites are mapped to certain cultures and only products containing price and name data for that culture will be synced
   * Adds a store state progress entry for each site progress. User may choose to use these or not
   * @param productsQuery the product query as it should be sent to the backend including cultures
   * @param marketHandlesBySiteId - markets that should be synched indexed by siteId. to update the product only in shopify, pass an object with the siteId and an empty market handle array
   * @param productSyncAction whether to delete or publish the selected products
   * @returns progress result
   */
  async startECommerceSync(payload: ECommerceSyncPayload) {
    this.store.clearProgress('web-sync')

    this.store.setProgressById({
      progressType: 'web-sync',
      currentAction: `Synkronisering startar...`,
    })

    this.wsConnection = await ApiService.ws(`products-sync/e-commerce`, undefined, payload, undefined)

    return new Promise<void>((resolve, reject) => {
      if (this.wsConnection) {
        this.wsConnection.onmessage = (e) => {
          const report: WebSocketProgressReport<StringArrayObject, Partial<Record<Country, StringArrayObject>>> = JSON.parse(e.data)

          const progress: Progress = {
            progressType: 'web-sync',
            id: report.store,
            currentAction: report.msg,
          }

          if (report.status === 'starting') {
            if (!report.store && report.progressPercent !== undefined) {
              progress.currentAction = `Förbereder artiklar och bilder... ${Math.round(report.progressPercent)}%`
            }
          } else if (report.status === 'inprogress') {
            if (report.progressPercent !== undefined) {
              progress.percentage = report.progressPercent
            }
          } else if (report.status === 'error') {
            progress.successful = false
            progress.finished = true
          } else if (report.status === 'finished') {
            progress.data = report.data
            progress.successful = true
            progress.finished = true
            progress.endDate = new Date()

            if (report.metadata) {
              progress.children = Object.entries(report.metadata).map(([market, marketData]) => ({
                id: market,
                successful: true,
                finished: true,
                data: marketData,
              }))
            }

            if (!report.store) {
              progress.currentAction = payload.productSyncAction
            }
          } else {
            console.log('received report with invalid status', report)
            return
          }
          this.store.setProgressById(progress)
        }

        this.wsConnection.onclose = (e) => {
          if (e.code !== 1000) {
            for (const progress of this.store.activeProgresses['web-sync']) {
              if (!progress.finished) {
                progress.successful = false
              }
            }

            this.store.setProgressById({
              progressType: 'web-sync',
              successful: false,
              finished: true,
              currentAction: `Synkronisering misslyckades. Felkod: ${e.code + (e.reason ? ' - ' : '') + e.reason}`,
            })
          }

          resolve()
        }
      } else {
        reject('No WS connection')
      }
    })
  }

  abortProductsSync() {
    this.wsConnection?.send('abort')
    this.store.abortProgress('web-sync')
  }

  async getProductsSiteSyncPreview(query?: PimLocationQuery, ignoredRules?: IgnorableRule[]): Promise<SyncPreview | undefined> {
    try {
      const queryParams = new URLSearchParams(query as Record<string, string>)
      if (ignoredRules) {
        queryParams.set('ignoredRules', ignoredRules.join(','))
      }

      return await ApiService.get(`products-sync/ecommerce-preview?${queryParams}&${DataService.getParams()}`)
    } catch (err: any) {
      this.store.addMessage(new Message('Kunde inte hämta artiklar', err, 'danger'))
    }
  }

  getAvailableECommerceSites = async () => ApiService.get<Site[]>('products-sync/available-ecommerce-sites')
}
export default new SyncService()
